/**
 * Outstanding receivables and payables, with ageing.
 *
 * A bill is not a row in the export — it is the NET of every allocation that
 * names it. `New Ref` opens it, `Agst Ref` settles it, and both live inside
 * ledger entries scattered across different vouchers. Summing invoices alone
 * would show every paid bill as still outstanding.
 */
import type { Snapshot } from "@tally/shared";

import { indexGroups, isPayable, isReceivable, money, sum } from "./groups.js";
import type { GroupIndex } from "./groups.js";

export type OutstandingBill = {
  ledger: string;
  billName: string;
  /** signed: negative = they owe us (receivable), positive = we owe them */
  balance: number;
  /** always positive — what is actually owed */
  amount: number;
  kind: "receivable" | "payable";
  /** earliest voucher that touched this bill */
  billDate: string;
  dueDate: string;
  daysOverdue: number;
  bucket: AgeingBucket;
};

export const AGEING_BUCKETS = ["0-30", "31-60", "61-90", "90+"] as const;
export type AgeingBucket = (typeof AGEING_BUCKETS)[number];

export type PartyOutstanding = {
  ledger: string;
  kind: "receivable" | "payable";
  total: number;
  buckets: Record<AgeingBucket, number>;
  bills: OutstandingBill[];
};

export type OutstandingReport = {
  asOf: string;
  receivables: PartyOutstanding[];
  payables: PartyOutstanding[];
  totalReceivable: number;
  totalPayable: number;
  receivableBuckets: Record<AgeingBucket, number>;
  payableBuckets: Record<AgeingBucket, number>;
  /** balances on bill-wise parties that no bill accounts for */
  unallocated: Array<{ ledger: string; amount: number; kind: "receivable" | "payable" }>;
};

function emptyBuckets(): Record<AgeingBucket, number> {
  return { "0-30": 0, "31-60": 0, "61-90": 0, "90+": 0 };
}

export function bucketFor(daysOverdue: number): AgeingBucket {
  if (daysOverdue <= 30) return "0-30";
  if (daysOverdue <= 60) return "31-60";
  if (daysOverdue <= 90) return "61-90";
  return "90+";
}

function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.floor((b - a) / 86_400_000);
}

type Accumulator = {
  ledger: string;
  billName: string;
  balance: number;
  billDate: string;
  dueDate: string | undefined;
};

export function deriveOutstanding(
  snapshot: Snapshot,
  asOf: string,
  index: GroupIndex = indexGroups(snapshot),
): OutstandingReport {
  const bills = new Map<string, Accumulator>();
  const allocatedByLedger = new Map<string, number>();

  for (const voucher of snapshot.vouchers) {
    if (voucher.isCancelled || voucher.isOptional) continue;

    for (const entry of voucher.ledgerEntries) {
      for (const allocation of entry.billAllocations) {
        const key = `${entry.ledger} ${allocation.billName}`;
        const existing = bills.get(key);

        if (existing) {
          existing.balance = money(existing.balance + allocation.amount);
          if (voucher.date < existing.billDate) existing.billDate = voucher.date;
          // The due date is set when the bill is opened; a settlement carries
          // none, so never let a later allocation clear one already known.
          if (!existing.dueDate && allocation.dueDate) existing.dueDate = allocation.dueDate;
        } else {
          bills.set(key, {
            ledger: entry.ledger,
            billName: allocation.billName,
            balance: money(allocation.amount),
            billDate: voucher.date,
            dueDate: allocation.dueDate,
          });
        }

        allocatedByLedger.set(
          entry.ledger,
          money((allocatedByLedger.get(entry.ledger) ?? 0) + allocation.amount),
        );
      }
    }
  }

  const byParty = new Map<string, PartyOutstanding>();

  for (const bill of bills.values()) {
    if (Math.abs(bill.balance) < 0.01) continue; // settled

    const kind: "receivable" | "payable" = bill.balance < 0 ? "receivable" : "payable";
    const dueDate = bill.dueDate ?? bill.billDate;
    const daysOverdue = Math.max(0, daysBetween(dueDate, asOf));
    const bucket = bucketFor(daysOverdue);

    const row: OutstandingBill = {
      ledger: bill.ledger,
      billName: bill.billName,
      balance: bill.balance,
      amount: Math.abs(bill.balance),
      kind,
      billDate: bill.billDate,
      dueDate,
      daysOverdue,
      bucket,
    };

    // A party can be a debtor on one bill and a creditor on another (an advance),
    // so key by both rather than assuming one side per ledger.
    const partyKey = `${bill.ledger} ${kind}`;
    let party = byParty.get(partyKey);
    if (!party) {
      party = { ledger: bill.ledger, kind, total: 0, buckets: emptyBuckets(), bills: [] };
      byParty.set(partyKey, party);
    }
    party.bills.push(row);
    party.total = money(party.total + row.amount);
    party.buckets[bucket] = money(party.buckets[bucket] + row.amount);
  }

  for (const party of byParty.values()) {
    party.bills.sort((a, b) => b.daysOverdue - a.daysOverdue || a.billName.localeCompare(b.billName));
  }

  const parties = [...byParty.values()].sort((a, b) => b.total - a.total);
  const receivables = parties.filter((p) => p.kind === "receivable");
  const payables = parties.filter((p) => p.kind === "payable");

  // A bill-wise party whose ledger balance is not fully explained by its bills
  // has an opening balance from before the snapshot window, or an entry booked
  // without an allocation. Silently dropping it understates what is owed.
  const unallocated: OutstandingReport["unallocated"] = [];
  for (const ledger of snapshot.ledgers) {
    if (!ledger.isBillWiseOn) continue;
    const receivable = isReceivable(index, ledger);
    const payable = isPayable(index, ledger);
    if (!receivable && !payable) continue;

    // Compare the closing balance against what the BILLS account for, not
    // against opening + movement. Those two are equal by construction, so
    // including the opening balance made the gap always zero — and an opening
    // balance carried in with no bill behind it is exactly the case this is
    // here to catch.
    const allocated = allocatedByLedger.get(ledger.name) ?? 0;
    const gap = money(ledger.closingBalance - allocated);
    if (Math.abs(gap) < 0.01) continue;

    unallocated.push({
      ledger: ledger.name,
      amount: Math.abs(gap),
      kind: gap < 0 ? "receivable" : "payable",
    });
  }

  return {
    asOf,
    receivables,
    payables,
    totalReceivable: sum(receivables.map((p) => p.total)),
    totalPayable: sum(payables.map((p) => p.total)),
    receivableBuckets: mergeBuckets(receivables),
    payableBuckets: mergeBuckets(payables),
    unallocated: unallocated.sort((a, b) => b.amount - a.amount),
  };
}

function mergeBuckets(parties: PartyOutstanding[]): Record<AgeingBucket, number> {
  const total = emptyBuckets();
  for (const party of parties) {
    for (const bucket of AGEING_BUCKETS) {
      total[bucket] = money(total[bucket] + party.buckets[bucket]);
    }
  }
  return total;
}
